import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { api } from "../../lib/api";

const FALLBACK_METRICS = [
  { value: 24, suffix: "M+", label: "Peak Instagram Views Per Quarter" },
  { value: 500, suffix: "K+", label: "Average YouTube Views", note: "Long-form & short-form combined" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-gold leading-none text-[clamp(5rem,13vw,10.5rem)]" data-testid={`reach-counter-${suffix.replace(/[^A-Za-z]/g, "")}`}>
      {display}
      <span className="text-gold-bright">{suffix}</span>
    </span>
  );
};

export default function Reach() {
  const [metrics, setMetrics] = useState(FALLBACK_METRICS);

  useEffect(() => {
    api.getConfig().then((cfg) => cfg?.metrics?.length && setMetrics(cfg.metrics)).catch(() => {});
  }, []);

  return (
    <section id="reach" className="relative py-24 md:py-36 bg-wood border-b border-woodborder" data-testid="reach-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Eyebrow index="02" label="AUDIENCE" />
          <h2 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-[0.85]" data-testid="reach-heading">
            THE <span className="text-outline-tan">REACH</span>
          </h2>
          <p className="mt-5 max-w-lg text-sand font-light">A statement of cultural reach — not a dashboard. The numbers the culture already knows about.</p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-woodborder border border-woodborder">
          {metrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.15} className="bg-wood">
              <div className="group p-8 md:p-14 h-full transition-colors duration-500 hover:bg-card" data-testid={`reach-stat-${i}`}>
                <Counter value={m.value} suffix={m.suffix} />
                <div className="mt-6 h-px w-16 bg-burnt transition-all duration-500 group-hover:w-28" />
                <p className="mt-5 font-editorial font-semibold text-cream text-lg md:text-xl uppercase tracking-wide">{m.label}</p>
                {m.note && <p className="mt-2 font-mono text-[11px] tracking-[0.15em] text-clay">{m.note}</p>}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 font-mono text-[11px] tracking-[0.25em] text-clay" data-testid="reach-footnote">
            THE CULTURE IS WATCHING. THE NUMBERS JUST KEEP THE RECEIPTS.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
